import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ExternalLink, Book, Code, Terminal, FileText, Cpu, Rocket } from "lucide-react";

const Docs = () => {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "DeepSeek Documentation - API Guides & Resources",
    "description": "Complete documentation for DeepSeek AI. API reference, integration guides, code examples, and best practices for developers.",
    "url": "https://deepseek.ai/docs"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://deepseek.ai" },
      { "@type": "ListItem", "position": 2, "name": "Documentation", "item": "https://deepseek.ai/docs" }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I get a DeepSeek API key?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sign up at platform.deepseek.com, navigate to API Keys section, and create a new key. The API is compatible with OpenAI's format."
        }
      },
      {
        "@type": "Question",
        "name": "Is the DeepSeek API compatible with OpenAI?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, DeepSeek provides an OpenAI-compatible API. You can use existing OpenAI client libraries by changing the base URL to api.deepseek.com."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Head>
        <title>DeepSeek API Docs 2026 — Quickstart &amp; Examples</title>
        <meta name="description" content="DeepSeek API documentation: 5-minute quickstart, Python &amp; Node.js code examples, authentication, rate limits and a drop-in OpenAI-compatible migration guide." />
        <link rel="canonical" href="https://deepseek.ai/docs" />
        <meta property="og:title" content="DeepSeek Documentation - API Reference & Guides" />
        <meta property="og:description" content="Complete DeepSeek documentation: API reference, integration guides, and code examples." />
        <meta property="og:url" content="https://deepseek.ai/docs" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek Documentation - API Reference & Guides" />
        <meta name="twitter:description" content="Complete DeepSeek documentation: API reference, integration guides, and code examples." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <main className="container mx-auto px-4 py-8">
        <Breadcrumbs currentPage="Documentation" />

        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">DeepSeek Documentation</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
            Everything you need to integrate DeepSeek AI into your applications. 
            OpenAI-compatible API makes migration simple.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <Button size="lg" asChild>
              <a href="https://api-docs.deepseek.com" target="_blank" rel="noopener noreferrer">
                Official API Docs <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="https://github.com/deepseek-ai" target="_blank" rel="noopener noreferrer">
                GitHub <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
          </div>
        </section>

        {/* Quick Links */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Documentation Resources</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <Rocket className="w-10 h-10 text-primary mb-2" />
                <CardTitle>Getting Started</CardTitle>
                <CardDescription>Quick start guide for DeepSeek API</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Learn how to set up your account, get an API key, and make your first API call in minutes.
                </p>
                <Button variant="link" className="p-0" asChild>
                  <a href="https://api-docs.deepseek.com/quick_start" target="_blank" rel="noopener noreferrer">
                    Read Guide <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <Code className="w-10 h-10 text-primary mb-2" />
                <CardTitle>API Reference</CardTitle>
                <CardDescription>Complete API documentation</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Full API reference including endpoints, parameters, response formats, and error codes.
                </p>
                <Button variant="link" className="p-0" asChild>
                  <a href="https://api-docs.deepseek.com/api/deepseek-api" target="_blank" rel="noopener noreferrer">
                    View Reference <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <Cpu className="w-10 h-10 text-primary mb-2" />
                <CardTitle>Model Overview</CardTitle>
                <CardDescription>Available models and capabilities</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Compare DeepSeek-V3, R1, and other models. Learn about context windows, capabilities, and use cases.
                </p>
                <Button variant="link" className="p-0" asChild>
                  <Link to="/deepseek-r1">
                    Explore Models
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <Terminal className="w-10 h-10 text-primary mb-2" />
                <CardTitle>Code Examples</CardTitle>
                <CardDescription>Sample code and integrations</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Python, JavaScript, and other language examples for common use cases.
                </p>
                <Button variant="link" className="p-0" asChild>
                  <a href="https://github.com/deepseek-ai/DeepSeek-V3" target="_blank" rel="noopener noreferrer">
                    View Examples <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <Book className="w-10 h-10 text-primary mb-2" />
                <CardTitle>Tutorials</CardTitle>
                <CardDescription>Step-by-step guides</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Build chatbots, RAG systems, and AI applications with DeepSeek.
                </p>
                <Button variant="link" className="p-0" asChild>
                  <Link to="/blog">
                    Read Tutorials
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <FileText className="w-10 h-10 text-primary mb-2" />
                <CardTitle>Research Papers</CardTitle>
                <CardDescription>Technical papers and benchmarks</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Read the technical papers behind DeepSeek models and their performance benchmarks.
                </p>
                <Button variant="link" className="p-0" asChild>
                  <a href="https://arxiv.org/search/?searchtype=author&query=DeepSeek" target="_blank" rel="noopener noreferrer">
                    View Papers <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Quick Start Code Example */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Quick Start Example</h2>
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Terminal className="w-5 h-5" />
                  Python Example
                </CardTitle>
                <CardDescription>Make your first API call with Python</CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{`from openai import OpenAI

# DeepSeek uses OpenAI-compatible API
client = OpenAI(
    api_key="your-api-key",
    base_url="https://api.deepseek.com"
)

response = client.chat.completions.create(
    model="deepseek-chat",  # or "deepseek-reasoner"
    messages=[
        {"role": "system", "content": "You are a helpful assistant."},
        {"role": "user", "content": "Hello!"}
    ]
)

print(response.choices[0].message.content)`}</code>
                </pre>
                <p className="text-sm text-muted-foreground mt-4">
                  <strong>Note:</strong> DeepSeek's API is fully compatible with OpenAI's client libraries. 
                  Just change the base URL and use your DeepSeek API key.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do I get a DeepSeek API key?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Sign up at{" "}
                  <a href="https://platform.deepseek.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    platform.deepseek.com
                  </a>, navigate to the API Keys section, and create a new key. The process takes less than a minute.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is the DeepSeek API compatible with OpenAI?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes, DeepSeek provides an OpenAI-compatible API. You can use existing OpenAI client libraries 
                  (Python, JavaScript, etc.) by simply changing the base URL to <code className="bg-muted px-1 rounded">api.deepseek.com</code>.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What models are available via the API?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  The API provides access to DeepSeek-V3 (general-purpose chat) and DeepSeek-R1 (reasoning model). 
                  Use <code className="bg-muted px-1 rounded">deepseek-chat</code> for V3 and{" "}
                  <code className="bg-muted px-1 rounded">deepseek-reasoner</code> for R1.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-muted/50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold mb-4">Ready to Build with DeepSeek?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Get your API key and start building AI-powered applications today.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" asChild>
              <a href="https://platform.deepseek.com" target="_blank" rel="noopener noreferrer">
                Get API Key <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/pricing">View Pricing</Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Docs;